import React from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, ChevronLeft, ChevronRight, ShoppingBag, ZoomIn } from "lucide-react";
import { trackViewStyle } from "../lib/analytics";

export default function StylePreview({ styles, currentId, open, onClose, onChoose }) {
  const index = React.useMemo(
    () => Math.max(0, styles.findIndex((s) => s.id === currentId)),
    [styles, currentId]
  );
  const [i, setI] = React.useState(index);
  const [zoomed, setZoomed] = React.useState(false);

  React.useEffect(() => {
    setI(index);
    setZoomed(false);
  }, [index, open]);

  const style = styles[i];

  React.useEffect(() => {
    if (open && style) trackViewStyle(style.id, style.name);
  }, [open, style]);

  const prev = React.useCallback(
    () => setI((v) => (v - 1 + styles.length) % styles.length),
    [styles.length]
  );
  const next = React.useCallback(
    () => setI((v) => (v + 1) % styles.length),
    [styles.length]
  );

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (zoomed) setZoomed(false);
        else onClose();
        return;
      }
      if (zoomed) return; // disable arrow nav while zoomed
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next, onClose, zoomed]);

  if (!style) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="!max-w-[1400px] w-[97vw] max-h-[95vh] p-0 gap-0 bg-[#f5f1e8] border-[#e8ddc3] overflow-hidden [&>button:last-child]:hidden"
        aria-describedby={undefined}
      >
        <div className="grid md:grid-cols-[1.8fr_1fr] max-h-[95vh] overflow-y-auto md:overflow-visible">
          {/* Image area */}
          <div className="relative bg-[#ece3cc] aspect-square md:aspect-auto md:min-h-[85vh] flex items-center justify-center p-2 md:p-4">
            <button
              type="button"
              onClick={() => setZoomed(true)}
              className="group/img relative w-full h-full flex items-center justify-center cursor-zoom-in focus:outline-none"
              aria-label="Ver imagen a pantalla completa"
            >
              <img
                src={style.image}
                alt={style.name}
                className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-300 group-hover/img:scale-[1.01]"
                loading="eager"
              />
              <span className="pointer-events-none absolute bottom-4 right-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1a1816]/80 text-white text-xs backdrop-blur-sm opacity-0 group-hover/img:opacity-100 transition-opacity">
                <ZoomIn className="h-3.5 w-3.5 text-[#d4b97a]" />
                Pantalla completa
              </span>
            </button>

            {/* Prev / Next */}
            {styles.length > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Anterior"
                  data-testid="style-preview-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white text-[#1a1816] shadow-md flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={next}
                  aria-label="Siguiente"
                  data-testid="style-preview-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white text-[#1a1816] shadow-md flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}

            <div className="absolute top-4 left-4 px-3 py-1 text-[11px] rounded-full bg-white/90 backdrop-blur text-[#3a3228] border border-[#e8ddc3]">
              {style.size || "20×15×3 cm"}
            </div>
          </div>

          {/* Info area */}
          <div className="p-7 md:p-9 flex flex-col">
            <button
              onClick={onClose}
              aria-label="Cerrar"
              data-testid="style-preview-close"
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/80 hover:bg-white border border-[#e8ddc3] text-[#1a1816] flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="text-[11px] tracking-[0.25em] uppercase text-[#b59557]">
              Estilo
            </div>
            <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[#1a1816] leading-tight">
              {style.name}
            </h3>
            <p className="mt-4 text-[#4a4136] leading-relaxed">
              {style.description}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-[#e8ddc3] bg-white/70 px-4 py-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#b59557]">
                  Tamaño
                </div>
                <div className="mt-1 font-serif text-base text-[#1a1816]">
                  {style.size || "20×15×3 cm"}
                </div>
              </div>
              <div className="rounded-xl border border-[#e8ddc3] bg-white/70 px-4 py-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#b59557]">
                  Material
                </div>
                <div className="mt-1 font-serif text-base text-[#1a1816]">
                  Taco de madera blanca
                </div>
              </div>
            </div>

            <div className="mt-auto pt-8 flex items-center justify-between">
              <div>
                <div className="font-serif italic text-2xl text-[#8a7340]">
                                  {style.price || "29,50€"}
                </div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#7a6a48]">
                  Envío incluido
                </div>
              </div>
              <button
                onClick={() => onChoose(style)}
                data-testid="style-preview-choose"
                className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
              >
                <ShoppingBag className="h-4 w-4" />
                Elegir este estilo
              </button>
            </div>

            {/* Thumbnails */}
            {styles.length > 1 && (
              <div className="mt-7 pt-5 border-t border-[#e8ddc3]">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#8a7340] mb-3">
                  Otros estilos
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {styles.map((s, idx) => (
                    <button
                      key={s.id}
                      onClick={() => setI(idx)}
                      className={`relative flex-shrink-0 h-14 w-14 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === i
                          ? "border-[#b59557] ring-2 ring-[#b59557]/30"
                          : "border-[#e8ddc3] hover:border-[#d6cdb9]"
                      }`}
                      aria-label={s.name}
                      title={s.name}
                    >
                      <img
                        src={s.image}
                        alt={s.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fullscreen image overlay */}
        {zoomed && (
          <div
            className="fixed inset-0 z-[100] bg-[#0a0908]/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200"
            onClick={() => setZoomed(false)}
            role="dialog"
            aria-label="Vista a pantalla completa"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setZoomed(false);
              }}
              aria-label="Cerrar pantalla completa"
              className="absolute top-5 right-5 h-12 w-12 rounded-full bg-white/95 hover:bg-white text-[#1a1816] flex items-center justify-center shadow-2xl transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <div
              className="absolute top-5 left-5 px-4 py-2 rounded-full bg-white/15 backdrop-blur text-white text-xs tracking-[0.2em] uppercase border border-white/20"
            >
              {style.name} · 20×15×3 cm
            </div>
            <img
              src={style.image}
              alt={style.name}
              className="max-w-[96vw] max-h-[92vh] w-auto h-auto object-contain cursor-zoom-out"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
